import $ from 'jquery';

/**
 * Vehicle card image gallery.
 *
 * Builds a small gallery inside each card's .vehicle__image from the photo
 * URLs output by parts/inventory/cards/_vehicle-card-customize.twig:
 *  - prev/next arrows (revealed on hover, always shown on touch devices)
 *  - image count dots, styled like the VDP gallery (revealed on hover; on
 *    touch devices dimmed until the user moves through the images)
 *  - on the last image the next arrow turns brand colour with a "View" label
 *    and links through to the VDP
 *  - swipe left/right on touch devices, except inside a Slick slider (the
 *    homepage cards are already swipeable)
 *
 * Extra images are only requested once the user interacts with a card.
 *
 * Event handlers are delegated and all state lives on the DOM, so cards that
 * Slick clones or the SRP re-renders keep working.
 */

const MAX_PHOTOS = 6;
const SWIPE_THRESHOLD = 40;
const INTERACTING_TIMEOUT = 1500;

const interactingTimers = new WeakMap();

const chevron = (direction) =>
  `<svg class="vehicle-gallery__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${
    direction === 'left' ? 'M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z' : 'M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z'
  }"/></svg>`;

function getVdpUrl(gallery) {
  const link = gallery.closest('a[href]');
  return link ? link.href : null;
}

function loadSlide(gallery, index) {
  const slide = gallery.querySelectorAll('.vehicle-gallery__slide')[index];
  const img = slide && slide.querySelector('img[data-src]');
  if (!img) return;

  img.addEventListener('load', () => slide.classList.add('is-loaded'), { once: true });
  img.addEventListener('error', () => slide.classList.add('is-error'), { once: true });
  img.src = img.dataset.src;
  img.removeAttribute('data-src');
}

// Flags the gallery while the user is moving through it (see .is-interacting).
function markInteracting(gallery) {
  gallery.classList.add('is-interacting');
  clearTimeout(interactingTimers.get(gallery));
  interactingTimers.set(
    gallery,
    setTimeout(() => gallery.classList.remove('is-interacting'), INTERACTING_TIMEOUT),
  );
}

function goTo(gallery, index) {
  const total = parseInt(gallery.dataset.total, 10);
  const current = Math.max(0, Math.min(index, total - 1));

  gallery.dataset.index = current;
  gallery.style.setProperty('--gallery-index', current);
  gallery.classList.toggle('is-first', current === 0);
  gallery.classList.toggle('is-last', current === total - 1);
  gallery
    .querySelector('.vehicle-gallery__arrow--next')
    .setAttribute('aria-label', current === total - 1 ? 'View vehicle' : 'Next photo');

  gallery.querySelectorAll('.vehicle-gallery__dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === current);
  });
  markInteracting(gallery);

  // Load the current image and preload the next one.
  loadSlide(gallery, current);
  loadSlide(gallery, current + 1);
}

function buildGallery(card) {
  const source = card.querySelector('.vehicle-gallery-photos');
  const frame = card.querySelector('.vehicle__image');
  const primary = frame && frame.querySelector(':scope > img');
  if (!source || !primary) return;

  let photos = [];
  try {
    photos = JSON.parse(source.dataset.photos);
  } catch (e) {
    return;
  }
  source.remove();

  photos = photos.slice(0, MAX_PHOTOS - 1);
  if (!photos.length) return;

  const total = photos.length + 1;
  const gallery = document.createElement('div');
  gallery.className = 'vehicle-gallery is-first';
  gallery.dataset.total = total;
  gallery.dataset.index = 0;

  const track = document.createElement('div');
  track.className = 'vehicle-gallery__track';

  const firstSlide = document.createElement('div');
  firstSlide.className = 'vehicle-gallery__slide is-loaded';
  firstSlide.appendChild(primary);
  track.appendChild(firstSlide);

  photos.forEach((src) => {
    const slide = document.createElement('div');
    slide.className = 'vehicle-gallery__slide';
    const img = document.createElement('img');
    img.alt = primary.alt;
    img.decoding = 'async';
    img.dataset.src = src;
    slide.appendChild(img);
    track.appendChild(slide);
  });

  const dots = Array.from({ length: total }, (_, i) =>
    `<span class="vehicle-gallery__dot${i === 0 ? ' is-active' : ''}"></span>`,
  ).join('');

  gallery.appendChild(track);
  gallery.insertAdjacentHTML(
    'beforeend',
    `<button type="button" class="vehicle-gallery__arrow vehicle-gallery__arrow--prev" aria-label="Previous photo">${chevron('left')}</button>
    <button type="button" class="vehicle-gallery__arrow vehicle-gallery__arrow--next" aria-label="Next photo">
      <span class="vehicle-gallery__view">View</span>${chevron('right')}
    </button>
    <div class="vehicle-gallery__dots" aria-hidden="true">${dots}</div>`,
  );

  frame.prepend(gallery);
  frame.classList.add('has-gallery');
}

export function applyCardGalleries() {
  document.querySelectorAll('.vehicle').forEach(buildGallery);
}

// Arrows
$(document).on('click', '.vehicle-gallery__arrow', function (e) {
  e.preventDefault();
  e.stopPropagation();

  const gallery = this.closest('.vehicle-gallery');
  const index = parseInt(gallery.dataset.index, 10);
  const isNext = this.classList.contains('vehicle-gallery__arrow--next');

  if (isNext && gallery.classList.contains('is-last')) {
    const url = getVdpUrl(gallery);
    if (url) window.location.href = url;
    return;
  }

  goTo(gallery, isNext ? index + 1 : index - 1);
});

// Preload the second image as soon as the user shows interest in a card.
$(document).on('mouseenter focusin touchstart', '.vehicle-gallery', function () {
  loadSlide(this, 1);
});

// Swipe (touch devices, outside Slick sliders)
let swipe = null;

$(document).on('touchstart', '.vehicle-gallery', function (e) {
  if (this.closest('.slick-slider') || e.originalEvent.touches.length > 1) return;
  const touch = e.originalEvent.touches[0];
  swipe = { gallery: this, x: touch.clientX, y: touch.clientY, dx: 0, horizontal: null };
});

$(document).on('touchmove', '.vehicle-gallery', function (e) {
  if (!swipe || swipe.gallery !== this) return;
  const touch = e.originalEvent.touches[0];
  const dx = touch.clientX - swipe.x;
  const dy = touch.clientY - swipe.y;

  if (swipe.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
    swipe.horizontal = Math.abs(dx) > Math.abs(dy);
    if (swipe.horizontal) this.classList.add('is-dragging');
  }
  if (!swipe.horizontal) return;
  markInteracting(this);

  // Resist when dragging past the first or last image.
  const atEdge =
    (dx > 0 && this.classList.contains('is-first')) || (dx < 0 && this.classList.contains('is-last'));
  swipe.dx = atEdge ? dx / 3 : dx;
  this.style.setProperty('--gallery-drag', `${swipe.dx}px`);
});

$(document).on('touchend touchcancel', '.vehicle-gallery', function () {
  if (!swipe || swipe.gallery !== this) return;
  const { dx, horizontal } = swipe;
  swipe = null;

  this.classList.remove('is-dragging');
  this.style.setProperty('--gallery-drag', '0px');
  if (!horizontal) return;

  // Swallow the click that may follow a swipe so it doesn't open the VDP.
  this.dataset.swiped = '1';
  setTimeout(() => delete this.dataset.swiped, 300);

  if (Math.abs(dx) > SWIPE_THRESHOLD) {
    const index = parseInt(this.dataset.index, 10);
    goTo(this, dx < 0 ? index + 1 : index - 1);
  }
});

$(document).on('click', '.vehicle-gallery', function (e) {
  if (this.dataset.swiped) {
    e.preventDefault();
    e.stopPropagation();
  }
});
